import { AlertCircleIcon } from 'lucide-react-native';
import { Controller, type Control, type FieldValues } from 'react-hook-form';

import { SurveyOtherTextInput } from './SurveyOtherTextInput';
import { SurveyQuestionLabel } from './SurveyQuestionLabel';
import {
  getSurveyOtherText,
  hasOtherItem,
  isOtherSelected,
  SURVEY_OTHER_VALUE,
  type SurveyElement,
} from '../../model/surveySchema';

import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
} from '@/components/ui/form-control';
import { CircleIcon } from '@/components/ui/icon';
import { Radio, RadioGroup, RadioIcon, RadioIndicator, RadioLabel } from '@/components/ui/radio';
import { TOUCH_TARGET_MIN_H } from '@/util/accessibleTouchTarget';

/**
 * Resets the invalid border the form control cascades onto every option.
 *
 * A rejected group otherwise turns all of its circles red, which reads as each
 * option being wrong rather than one answer being missing. The error message
 * and icon still carry the state, so this removes a misleading cue rather than
 * the only cue.
 */
const INVALID_INDICATOR_RESET = 'data-[invalid=true]:border-outline-400';

interface SurveyChoiceQuestionProps {
  element: SurveyElement;
  control: Control<FieldValues>;
  errorMessage?: string;
  /** Error for the Other free-text field, which is a separate form field from the choice. */
  otherErrorMessage?: string;
  hideRequiredMarker?: boolean;
  /** Order to render the element's choices in; the schema order when absent. */
  displayChoices?: string[];
}

/** Renders a single-choice `radiogroup` element as inline radio options. */
export function SurveyChoiceQuestion({
  element,
  control,
  errorMessage,
  otherErrorMessage,
  hideRequiredMarker,
  displayChoices,
}: SurveyChoiceQuestionProps) {
  const choices = displayChoices ?? element.choices ?? [];

  const showsOther = hasOtherItem(element);

  return (
    <Controller
      name={element.name}
      control={control}
      render={({ field: { onChange, value } }) => (
        <FormControl
          isInvalid={!!errorMessage}
          aria-required={element.isRequired}
          className="gap-2">
          <SurveyQuestionLabel element={element} hideRequiredMarker={hideRequiredMarker} />

          <RadioGroup
            value={typeof value === 'string' ? value : ''}
            onChange={onChange}
            aria-label={element.isRequired ? `${element.title}, required` : element.title}>
            {choices.map((choice) => (
              <Radio
                testID={`${element.name}-${choice}`}
                value={choice}
                key={choice}
                className={TOUCH_TARGET_MIN_H}>
                <RadioIndicator className={INVALID_INDICATOR_RESET}>
                  <RadioIcon as={CircleIcon} />
                </RadioIndicator>
                <RadioLabel>{choice}</RadioLabel>
              </Radio>
            ))}

            {showsOther ? (
              <Radio
                testID={`${element.name}-${SURVEY_OTHER_VALUE}`}
                value={SURVEY_OTHER_VALUE}
                key={SURVEY_OTHER_VALUE}
                aria-label={getSurveyOtherText(element)}
                className={TOUCH_TARGET_MIN_H}>
                <RadioIndicator className={INVALID_INDICATOR_RESET}>
                  <RadioIcon as={CircleIcon} />
                </RadioIndicator>
                <RadioLabel>{getSurveyOtherText(element)}</RadioLabel>
              </Radio>
            ) : null}
          </RadioGroup>

          {showsOther && isOtherSelected(value) ? (
            <SurveyOtherTextInput
              element={element}
              control={control}
              errorMessage={otherErrorMessage}
            />
          ) : null}

          <FormControlError>
            <FormControlErrorIcon as={AlertCircleIcon} />
            <FormControlErrorText>{errorMessage}</FormControlErrorText>
          </FormControlError>
        </FormControl>
      )}
    />
  );
}
